import React from "react";
import Modal from "react-modal";
import { Lock, Eye, EyeOff, X, AlertCircle } from "lucide-react";

const confirmModalStyles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(17, 24, 39, 0.45)",
    display: "grid",
    placeItems: "center",
    zIndex: 1200,
    padding: 16,
  },
  content: {
    position: "relative",
    inset: "auto",
    width: "100%",
    maxWidth: 500,
    borderRadius: 22,
    padding: 0,
    border: "1px solid #eef2f7",
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.22)",
    overflow: "hidden",
    background: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "Inter, sans-serif",
  },
};

export default function ConfirmIdentityModal({
  isOpen,
  loading = false,
  errorText = "",
  onClose, // cancelar
  onConfirm, // (password) => void
  onChangePassword,
}) {
  const [password, setPassword] = React.useState("");
  const [show, setShow] = React.useState(false);
  const inputRef = React.useRef(null);

  const canConfirm = password.trim().length > 0 && !loading;

  React.useEffect(() => {
    if (!isOpen) return;
    setPassword("");
    setShow(false);
    setTimeout(() => inputRef.current?.focus?.(), 80);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    if (errorText) {
      setTimeout(() => inputRef.current?.focus?.(), 50);
    }
  }, [isOpen, errorText]);

  const submit = () => {
    const val = password.trim();
    if (!val) return;
    onConfirm?.(val);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => (!loading ? onClose?.() : null)}
      shouldCloseOnOverlayClick={!loading}
      shouldCloseOnEsc={!loading}
      style={confirmModalStyles}
      ariaHideApp={false}
    >
      <div style={{ padding: "26px 28px 26px", position: "relative" }}>
        <button
          type="button"
          onClick={() => (!loading ? onClose?.() : null)}
          disabled={loading}
          aria-label="Cerrar"
          style={{
            position: "absolute",
            top: 16,
            right: 18,
            border: 0,
            background: "transparent",
            color: "#9CA3AF",
            cursor: loading ? "not-allowed" : "pointer",
            display: "grid",
            placeItems: "center",
          }}
        >
          <X size={20} />
        </button>

        {/* Icono circular rojo */}
        <div style={{ display: "grid", placeItems: "center", marginTop: 6 }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 999,
              background: "rgba(239, 68, 68, 0.12)",
              display: "grid",
              placeItems: "center",
              color: "#ef4444",
            }}
          >
            <Lock size={26} />
          </div>
        </div>

        <h2
          style={{
            textAlign: "center",
            margin: "14px 0 6px",
            fontSize: 28,
            fontWeight: 600,
            color: "#111827",
          }}
        >
          Confirmar identidad
        </h2>

        <p
          style={{
            textAlign: "center",
            margin: 0,
            color: "#6B7280",
            fontSize: 14,
            lineHeight: 1.4,
          }}
        >
          Por seguridad, ingresa tu contraseña para continuar.
        </p>

        {/* Input */}
        <div style={{ marginTop: 18 }}>
          <div style={{ position: "relative" }}>
            <span
              style={{
                position: "absolute",
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Lock size={18} />
            </span>

            <input
              ref={inputRef}
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                onChangePassword?.(e.target.value);
              }}
              disabled={loading}
              placeholder=" "
              onKeyDown={(e) => {
                if (e.key === "Enter" && canConfirm) submit();
                if (e.key === "Escape" && !loading) onClose?.();
              }}
              style={{
                width: "100%",
                height: 52,
                borderRadius: 14,
                border: "1.6px solid #dbe7ff",
                background: "#eaf2ff",
                outline: "none",
                paddingLeft: 44,
                paddingRight: 46,
                fontSize: 15,
                fontWeight: 700,
                color: "#0f172a",
                boxSizing: "border-box",
              }}
            />

            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              disabled={loading}
              aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                border: 0,
                background: "transparent",
                color: "#64748b",
                cursor: loading ? "not-allowed" : "pointer",
                display: "grid",
                placeItems: "center",
                padding: 6,
              }}
            >
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div
            style={{
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginTop: 10,
            }}
          >
            {!!errorText && (
              <div
                style={{
                  color: "#EF4444",
                  fontSize: 14,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <AlertCircle size={16} />
                {errorText}
              </div>
            )}
          </div>
        </div>

        {/* Botones */}
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={() => (!loading ? onClose?.() : null)}
            disabled={loading}
            style={{
              height: 48,
              borderRadius: 14,
              border: 0,
              background: "#f1f5f9",
              color: "#334155",
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              fontSize: 15,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={submit}
            disabled={!canConfirm}
            style={{
              height: 48,
              borderRadius: 14,
              border: 0,
              background: canConfirm ? "#3b82f6" : "#93c5fd",
              color: "#ffffff",
              fontWeight: 900,
              fontFamily: "Inter, sans-serif",
              fontSize: 15,
              cursor: !canConfirm ? "not-allowed" : "pointer",
              boxShadow: canConfirm
                ? "0 14px 30px rgba(59, 130, 246, 0.30)"
                : "none",
              transition: "all 160ms ease",
            }}
          >
            {loading ? "Confirmando..." : "Confirmar"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
